import { useState } from 'react';
import { toast } from 'react-toastify';

import { LABELS_BATCH_SIZE } from '@/constants/env';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { IResLabelsOK, LabelPageSizes } from '@/types/parcel/orders/labels.interface';

const triggerSilentDownload = (url: string, filename = 'etiquetas.pdf'): void => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

/**
 * Splits an array into sequential chunks of the given size.
 */
const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

interface UsePickupsDownloadReturn {
  isDownloadModalOpen: boolean;
  isDownloading: boolean;
  batchProgress: { current: number; total: number } | null;
  openDownloadModal: () => void;
  closeDownloadModal: () => void;
  downloadLabels: (trackingIds: string[], pageSize: LabelPageSizes) => void;
}

const usePickupsDownload = (): UsePickupsDownloadReturn => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(
    null,
  );

  const openDownloadModal = () => setIsDownloadModalOpen(true);
  const closeDownloadModal = () => setIsDownloadModalOpen(false);

  const downloadLabels = async (trackingIds: string[], pageSize: LabelPageSizes): Promise<void> => {
    const http = initializeHttpProvider();
    const batches = chunkArray(trackingIds, LABELS_BATCH_SIZE);
    const totalBatches = batches.length;
    const isBatched = totalBatches > 1;

    setIsDownloading(true);
    setBatchProgress(isBatched ? { current: 0, total: totalBatches } : null);

    let hasError = false;

    for (let i = 0; i < batches.length; i++) {
      if (isBatched) {
        setBatchProgress({ current: i + 1, total: totalBatches });
      }

      try {
        const res = await http.orders.labels({ pageSize, trackingIds: batches[i] });
        const response = res as IResLabelsOK;
        triggerSilentDownload(
          response.data.url,
          totalBatches > 1 ? `etiquetas-lote-${i + 1}.pdf` : 'etiquetas.pdf',
        );
      } catch (error) {
        hasError = true;
        toast.error(
          `Error al descargar etiquetas${isBatched ? ` (lote ${i + 1} de ${totalBatches})` : ''}`,
        );
        console.error('Failed to download label batch:', error);
        break;
      }
    }

    if (!hasError) {
      toast.success(
        isBatched
          ? `${totalBatches} lotes de etiquetas descargados correctamente`
          : 'Etiqueta descargada',
      );
      closeDownloadModal();
    }

    setIsDownloading(false);
    setBatchProgress(null);
  };

  return {
    isDownloadModalOpen,
    isDownloading,
    batchProgress,
    openDownloadModal,
    closeDownloadModal,
    downloadLabels,
  };
};

export default usePickupsDownload;
